import { Modal, Tooltip } from '@ds';
import classNames from 'classnames';
import { ICaseSubproject } from 'src/shared/types/Case';
import { useFormatDate } from 'src/utils/hooks/useFormatDate';
import StageNotes from '../StageNotes/StageNotes';
import { useScreenSize } from '@ds/utils/hooks/useScreenSize';
import styles from './SupplementalFilingsModal.module.scss';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';

interface ISupplementalFilingsModal {
  visible: boolean;
  caseName: string;
  subprojects: ICaseSubproject[];
  onModalClosed: () => void;
}

const DocumentsButton: React.FC<{
  tooltipTarget: string;
  documentIds: number[];
  onDocumentClick: () => void;
}> = ({ tooltipTarget, documentIds, onDocumentClick }) => {
  const isDisabled = documentIds.length === 0;

  return (
    <div
      className={classNames(styles['documents-button'], {
        [`${tooltipTarget}`]: true,
      })}
      onClick={onDocumentClick}
    >
      <div className={styles['documents-icon']}></div>
      <p>documents</p>
      {isDisabled && (
        <Tooltip target={`.${tooltipTarget}`} content="There are no documents for this case" position="left" />
      )}
    </div>
  );
};

const CaseStatus = ({ closedDate }: { closedDate: string }) => {
  const formattedDate = closedDate ? useFormatDate(closedDate) : null;

  return (
    <div className={styles['case-status']}>
      <div className={`${closedDate ? styles['case-status-tick-icon'] : styles['case-status-progress-icon']}`}></div>
      <span>{closedDate ? 'Completed: ' + formattedDate : 'In Progress'}</span>
    </div>
  );
};

const SupplementalFilingsModal: React.FC<ISupplementalFilingsModal> = ({
  visible,
  caseName,
  subprojects,
  onModalClosed,
}) => {
  const { navigate } = useNavigateWithSecureParams();
  const { isDesktop } = useScreenSize();

  return (
    <Modal
      visible={visible}
      onModalClosed={onModalClosed}
      className={styles['supplemental-fillings-modal-wrapper']}
      modalClassName={styles['supplemental-fillings-modal']}
      title={<h3>{caseName} supplemental filings</h3>}
    >
      <div className={styles['supplemental-fillings-body']}>
        {subprojects.map((sp, index) => {
          const subTypes =
            sp.subCaseType.length > 0 ? <p className={styles['sub-case-types']}>{sp.subCaseType.join(', ')}</p> : null;
          const notes =
            sp.notes?.map((note) => ({
              id: note.reportNoteId,
              detail: note.reportNotes,
              date: note.postedDate || note.postedDateOnly,
            })) || [];

          return (
            <div className={styles['sf-item']} key={index}>
              <div className={styles['sf-item-header']}>
                <div className="flex justify-between">
                  <div className={styles['case-name-and-status']}>
                    <span className={styles['case-name']}>{sp.caseType}</span>
                    {!isDesktop && subTypes}
                    <CaseStatus closedDate={sp.closedDate} />
                  </div>
                  <DocumentsButton
                    documentIds={sp.documentIds}
                    tooltipTarget={`documents-button${index}`}
                    onDocumentClick={() =>
                      navigate(`/case-documents`, {
                        secureParams: {
                          caseId: sp.caseId,
                        },
                      })
                    }
                  />
                </div>
                {isDesktop && subTypes}
              </div>
              {sp.notes?.length > 0 && <StageNotes notes={notes} />}
            </div>
          );
        })}
      </div>
    </Modal>
  );
};

export default SupplementalFilingsModal;
